/*
  Working copy: cart drawer subscription switcher.

  Intended future destination:
  - assets/hp8-custom-cart-drawer.js

  Depends on the theme's existing cart-drawer custom element and the Shopify
  Ajax cart change endpoint.
*/

class CartDrawerSubscription {
  constructor() {
    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    document.addEventListener('click', this.handleClick);
    document.addEventListener('change', this.handleChange);
  }

  handleClick(event) {
    const button = event.target.closest('[data-cart-drawer-selling-plan-button]');

    if (!button) {
      return;
    }

    event.preventDefault();

    const container = button.closest('[data-cart-drawer-subscription]');

    if (!container || button.disabled) {
      return;
    }

    this.updateSellingPlan(button.dataset.sellingPlanValue || '', container);
  }

  handleChange(event) {
    const select = event.target.closest('[data-cart-drawer-selling-plan-select]');

    if (!select) {
      return;
    }

    const container = select.closest('[data-cart-drawer-subscription]');

    if (!container) {
      return;
    }

    this.updateSellingPlan(select.value || '', container);
  }

  buildRequest(body) {
    const baseConfig = typeof fetchConfig === 'function'
      ? fetchConfig()
      : {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        };

    return {
      ...baseConfig,
      method: 'POST',
      headers: {
        ...(baseConfig.headers || {}),
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    };
  }

  getSectionIds(cartDrawer) {
    if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') {
      return [];
    }

    return cartDrawer.getSectionsToRender().map((section) => section.section);
  }

  setBusy(container, isBusy) {
    container.classList.toggle('is-loading', isBusy);
    container.setAttribute('aria-busy', isBusy ? 'true' : 'false');

    const select = container.querySelector('[data-cart-drawer-selling-plan-select]');
    const spinner = container.querySelector('[data-cart-drawer-subscription-spinner]');
    const buttons = container.querySelectorAll('[data-cart-drawer-selling-plan-button]');

    if (select) {
      select.disabled = isBusy;
    }

    buttons.forEach((button) => {
      button.disabled = isBusy;
    });

    if (spinner) {
      spinner.classList.toggle('hidden', !isBusy);
    }
  }

  syncSelection(container, value) {
    const normalizedValue = value == null ? '' : String(value);
    const select = container.querySelector('[data-cart-drawer-selling-plan-select]');
    const buttons = container.querySelectorAll('[data-cart-drawer-selling-plan-button]');

    container.dataset.currentValue = normalizedValue;

    if (select) {
      select.value = normalizedValue;
      select.dataset.currentValue = normalizedValue;
    }

    buttons.forEach((button) => {
      const buttonValue = button.dataset.sellingPlanValue || '';
      const isSelected = buttonValue === normalizedValue;

      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });
  }

  setStatus(container, message, isError = false) {
    const status = container.querySelector('[data-cart-drawer-subscription-status]');

    if (!status) {
      return;
    }

    status.textContent = message || '';
    status.classList.toggle('hidden', !message);
    status.classList.toggle('is-error', isError);
  }

  announce(message) {
    const liveRegion = document.getElementById('cart-drawer-live-region-text');

    if (!liveRegion) {
      return;
    }

    liveRegion.textContent = message;
    liveRegion.setAttribute('aria-hidden', false);

    window.setTimeout(() => {
      liveRegion.setAttribute('aria-hidden', true);
    }, 1000);
  }

  async updateSellingPlan(nextValue, container) {
    const line = Number(container.dataset.line);
    const quantity = Number(container.dataset.quantity || 1);
    const previousValue = container.dataset.currentValue || '';
    const sellingPlan = nextValue === '' ? null : String(nextValue);
    const cartDrawer = document.querySelector('cart-drawer');
    const cartChangeUrl = routes.cart_change_url.endsWith('.js')
      ? routes.cart_change_url
      : `${routes.cart_change_url}.js`;
    const requestBody = {
      line,
      quantity,
      selling_plan: sellingPlan,
    };
    const sectionIds = this.getSectionIds(cartDrawer);

    if (sectionIds.length > 0) {
      requestBody.sections = sectionIds;
      requestBody.sections_url = window.location.pathname;
    }

    if ((sellingPlan || '') === previousValue) {
      return;
    }

    this.setBusy(container, true);
    this.syncSelection(container, sellingPlan);
    this.setStatus(container, 'Updating delivery option...');

    if (cartDrawer && typeof cartDrawer.enableLoading === 'function') {
      cartDrawer.enableLoading(line);
    }

    try {
      const response = await fetch(cartChangeUrl, this.buildRequest(requestBody));
      const state = await response.json();

      if (!response.ok) {
        throw new Error(state.description || state.message || 'Unable to update delivery option.');
      }

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.currentItemCount = -1;
        cartDrawer.renderContents(state, false, line, 'selling_plan', true);
      } else {
        this.setBusy(container, false);
        this.syncSelection(container, sellingPlan);
        this.setStatus(
          container,
          sellingPlan === null ? 'Switched to one-time purchase.' : 'Subscription updated.'
        );
      }

      this.announce(
        sellingPlan === null ? 'Switched to one-time purchase.' : 'Subscription updated.'
      );
    } catch (error) {
      this.syncSelection(container, previousValue);
      this.setBusy(container, false);
      this.setStatus(
        container,
        error && error.message ? error.message : 'Unable to update delivery option.',
        true
      );

      if (cartDrawer && typeof cartDrawer.disableLoading === 'function') {
        cartDrawer.disableLoading();
      }
    }
  }
}

if (!window.__cartDrawerSubscriptionInitialized) {
  window.__cartDrawerSubscriptionInitialized = true;
  new CartDrawerSubscription();
}
